import React from 'react';
import { Link } from 'react-router-dom';

const facts = [
  { stat: '70%', desc: 'reduction in headlight output from oxidized lenses' },
  { stat: '48hrs', desc: 'before bird droppings etch and permanently damage clear coat' },
  { stat: '99.9%', desc: 'of bacteria and viruses killed by steam at 212°F' },
  { stat: '$4,000', desc: 'premium a well-maintained car commands at trade-in' },
];

const topics = [
  {
    icon: '🌙',
    title: 'Windshield Clarity & Night Safety',
    beforePhoto: '/images/windshield-before.jpg',
    afterPhoto: '/images/windshield-after.jpg',
    bullets: [
      'Oil and film on glass scatter headlights into blinding halos',
      'Dirty windshields cut visible stopping distance by up to 30% at night',
      'Professional cleaning inside and out restores factory-new clarity',
    ],
    stat: { value: '30%', label: 'less visible stopping distance at night' },
  },
  {
    icon: '🌬️',
    title: 'Air Quality & Steam Cleaning',
    beforePhoto: '/images/steam-before.jpg',
    afterPhoto: '/images/steam-after.jpg',
    bullets: [
      'You spend 300+ hours/year in a sealed cabin that recirculates everything inside',
      'Dust mites, mold spores, and bacteria live in carpet fibers and seat fabric',
      'Steam at 212°F eliminates — not masks — bacteria, allergens, and viruses',
    ],
    stat: { value: '99.9%', label: 'of bacteria killed by professional steam' },
  },
  {
    icon: '💡',
    title: 'Headlight Oxidation',
    beforePhoto: '/images/headlight-before.jpg',
    afterPhoto: '/images/headlight-after.jpg',
    bullets: [
      'UV exposure turns polycarbonate lenses cloudy and yellow over time',
      'Oxidized headlights reduce light output by 40–70% — a real safety risk',
      'Restoration costs a fraction of $300–$800 replacement and looks just as good',
    ],
    stat: { value: '70%', label: 'light output lost to oxidized lenses' },
  },
  {
    icon: '🛡️',
    title: 'Paint & Wheel Protection',
    beforePhoto: '/images/wheel-before.jpg',
    afterPhoto: '/images/wheel-after.jpg',
    bullets: [
      'Bird droppings are pH 3–4.5 — they etch clear coat permanently within 48 hours in heat',
      'Tree sap bonds molecularly to paint — removal costs $500–$2,500 when left untreated',
      'Brake dust is hot, charged metal that etches into aluminum alloy wheels permanently',
      'Clean wheels help you spot brake pad wear and rotor damage before it gets expensive',
    ],
    stat: { value: '48hrs', label: 'until bird droppings permanently etch your paint' },
  },
  {
    icon: '🧠',
    title: 'Mental Health & Your Space',
    beforePhoto: '/images/mental-before.jpg',
    afterPhoto: '/images/mental-after.jpg',
    bullets: [
      'Princeton research: clutter elevates cortisol and impairs focus — daily',
      'You drive every day — that environment directly affects your mental state',
      'A clean car signals discipline and intentionality to everyone who sees it',
    ],
    stat: { value: '300hrs', label: 'average time spent in your vehicle each year' },
  },
  {
    icon: '💰',
    title: 'Resale Value',
    hideBeforeAfter: true,
    graphImage: '/images/resale-graph.jpg',
    bullets: [
      'KBB: well-maintained vehicles sell for $1,500–$4,000 more than neglected ones',
      'Oxidized paint, stained interior, and odor are the top 3 lowball triggers',
      'Monthly maintenance costs less than dinner out — and protects your biggest asset',
    ],
    stat: { value: '$4,000', label: 'premium a maintained car commands at trade-in' },
  },
];


function TopicCard({ topic }) {
  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-2xl overflow-hidden">
      {/* Before / After photos or graph image */}
      {!topic.hideBeforeAfter ? (
        <div className="grid grid-cols-2 h-44">
          <div className="relative overflow-hidden">
            <span className="absolute top-2 left-2 z-10 bg-black/70 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider text-red-400 border border-red-400/30">Before</span>
            <img
              src={topic.beforePhoto}
              alt={`${topic.title} before`}
              className="w-full h-full object-cover"
              style={{ filter: 'brightness(0.9)' }}
            />
          </div>
          <div className="relative overflow-hidden border-l border-white/5">
            <span className="absolute top-2 left-2 z-10 bg-black/70 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider text-emerald-400 border border-emerald-400/30">After</span>
            <img
              src={topic.afterPhoto}
              alt={`${topic.title} after`}
              className="w-full h-full object-cover"
              style={{ filter: 'brightness(1.05) saturate(1.1)' }}
            />
          </div>
        </div>
      ) : topic.graphImage ? (
        <div className="h-44 overflow-hidden bg-white">
          <img src={topic.graphImage} alt={topic.title} className="w-full h-full object-cover object-top" />
        </div>
      ) : null}

      {/* Content */}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xl">{topic.icon}</span>
          <h2 className="text-base font-bold text-[#111827]">{topic.title}</h2>
        </div>
        <ul className="space-y-2 mb-4">
          {topic.bullets.map((b, i) => (
            <li key={i} className="flex gap-2 text-sm text-[#6B7280] leading-snug">
              <span className="text-[#68caf1] mt-0.5 shrink-0 font-bold">→</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <div className="bg-[#68caf1]/10 border border-[#68caf1]/20 rounded-lg px-4 py-2 flex items-baseline gap-2">
          <span className="text-[#68caf1] font-bold text-xl">{topic.stat.value}</span>
          <span className="text-[#6B7280] text-xs">{topic.stat.label}</span>
        </div>

      </div>
    </div>
  );
}

export default function EducationPage() {
  return (
    <div className="min-h-screen text-[#111827]">
      {/* Hero photo */}
      <section className="relative h-[50vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=1600&q=80"
          alt="Clean car"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-[#EAF0F6]" />
        <div className="absolute bottom-12 left-0 right-0 text-center px-6">
          <p className="text-[#68caf1] text-sm font-bold uppercase tracking-[0.2em] mb-3">Monarch Education</p>
          <h1 className="text-5xl md:text-6xl font-bold leading-tight">
            Clean Cars.<br /><span className="text-[#68caf1]">Safer Lives.</span>
          </h1>
        </div>
      </section>

      {/* Subtitle */}
      <section className="pt-10 pb-4 px-6 text-center">
        <p className="text-[#6B7280] text-lg md:text-xl leading-relaxed max-w-3xl mx-auto">
          Most people think detailing is about looks. It's not. A clean vehicle is safer, healthier, and worth more — here's the science.
        </p>
      </section>

      {/* Stats bar */}
      <section className="border-y border-gray-200 bg-gray-50 py-8 px-6">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {facts.map((f, i) => (
            <div key={i} className="text-center">
              <p className="text-3xl font-bold text-[#68caf1]">{f.stat}</p>
              <p className="text-[#6B7280] text-xs mt-1 leading-snug">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Topic cards grid */}
      <section className="py-12 px-6">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          {topics.map((topic, i) => (
            <TopicCard key={i} topic={topic} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 text-center bg-gradient-to-t from-[#68caf1]/10 to-transparent">
        <div className="max-w-xl mx-auto">
          <p className="text-[#68caf1] text-sm font-semibold tracking-widest uppercase mb-4">Ready to protect what matters?</p>
          <h2 className="text-4xl font-bold text-[#111827] mb-4">Your car deserves better.</h2>
          <p className="text-[#6B7280] mb-8">
            Monarch Detailing isn't just a clean car. It's safety, health, longevity, and the pride of driving something that reflects who you are.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/detail"
              className="px-8 py-4 bg-[#68caf1] text-black font-bold rounded-lg hover:bg-[#50b8df] transition-colors"
            >
              Book Your Interior Detail
            </Link>
          </div>
        </div>
      </section>

      <div className="h-16" />
    </div>
  );
}
